import * as React from 'react'
import { createPortal } from 'react-dom'
import { useTooltipBoundary } from '../context/TooltipBoundaryContext'

type TooltipProps = {
  text: string
  children: React.ReactNode
  disabled?: boolean

  blockedText?: string
  onClick?: () => void
  checked?: boolean
  className?: string
  boundaryRef?: React.RefObject<HTMLElement> | null // passed from outside
  showDot?: boolean
  asChild?: boolean
  align?: 'center' | 'start'
 
  maxWidth?: string

  portal?: boolean

  zIndex?: number
}

const Tooltip: React.FC<TooltipProps> = ({
  text,
  children,
  disabled = false,
  blockedText,
  onClick,
  className = '',
  checked = true,
  showDot = false,
  asChild = false,
  align = 'center',
  maxWidth,
  portal = false,
  zIndex,
}) => {
  const blocked = Boolean(blockedText)
  const label = blocked ? blockedText! : text

  const boundaryRef = useTooltipBoundary()
  const [visible, setVisible] = React.useState(false)
  const [hPos, setHPos] = React.useState<'center' | 'left' | 'right'>('center')
  const [vPos, setVPos] = React.useState<'bottom' | 'top'>('bottom')

  const tooltipRef = React.useRef<HTMLDivElement>(null)
  const triggerRef = React.useRef<HTMLButtonElement>(null)
  const [fixedPosition, setFixedPosition] = React.useState<{
    top: number
    left: number
  } | null>(null)


  const updateFixedPosition = React.useCallback(() => {
    const tipEl = tooltipRef.current
    const trgEl = triggerRef.current
    if (!tipEl || !trgEl) return

    const trgRect = trgEl.getBoundingClientRect()
    const tipRect = tipEl.getBoundingClientRect()
    const gap = 8
    const padding = 8

    let top = trgRect.bottom + gap
    if (top + tipRect.height > window.innerHeight - padding) {
      top = Math.max(padding, trgRect.top - gap - tipRect.height)
    }

    let left = trgRect.left + trgRect.width / 2 - tipRect.width / 2
    left = Math.min(
      Math.max(padding, left),
      Math.max(padding, window.innerWidth - padding - tipRect.width),
    )

    setFixedPosition({ top, left })
  }, [])

  
  React.useLayoutEffect(() => {
    if (!portal || !visible) return undefined

    updateFixedPosition()

    const reposition = () => updateFixedPosition()
    window.addEventListener('scroll', reposition, true)
    window.addEventListener('resize', reposition)
    return () => {
      window.removeEventListener('scroll', reposition, true)
      window.removeEventListener('resize', reposition)
    }
  }, [portal, visible, updateFixedPosition])

  const updatePosition = () => {
    const tipEl = tooltipRef.current
    const trgEl = triggerRef.current
    if (!tipEl || !trgEl) return

    const tipRect = tipEl.getBoundingClientRect()
    const tipW = tipRect.width
    const tipH = tipRect.height

    const trgRect = trgEl.getBoundingClientRect()


    const boundaryRect = boundaryRef?.current?.getBoundingClientRect() ?? {
      left: 0,
      right: window.innerWidth,
      top: 0,
      bottom: window.innerHeight,
    }

    const padding = 8
    const gap = 16 // mt-4 / mb-4

    // Vertical: bottom vs top
    const overflowBottom =
      trgRect.bottom + gap + tipH > boundaryRect.bottom - padding
    const overflowTop = trgRect.top - gap - tipH < boundaryRect.top + padding

    if (overflowBottom && !overflowTop) setVPos('top')
    else setVPos('bottom')

    // Horizontal: center vs left/right
    const triggerCenterX = (trgRect.left + trgRect.right) / 2
    const leftIfCentered = triggerCenterX - tipW / 2
    const rightIfCentered = triggerCenterX + tipW / 2

    if (rightIfCentered > boundaryRect.right - padding) setHPos('left')
    else if (leftIfCentered < boundaryRect.left + padding) setHPos('right')
    else setHPos('center')
  }

  const handleMouseEnter = () => {
    setVisible(true)
    if (!portal) {
      requestAnimationFrame(updatePosition)
    }
  }

  const handleMouseLeave = () => {
    setVisible(false)
    setHPos('center')
    setVPos('bottom')
    setFixedPosition(null)
  }

  const TriggerTag = asChild ? 'span' : 'button'

  // w-max: dymek jest pozycjonowany absolutnie, wiec przy zawijaniu "shrink-to-fit"
  // liczylby szerokosc od waskiego rodzica (sama ikona) i tekst zjechalby w pionowy
  // pasek - dopiero width:max-content + max-width daje normalny, zawijany blok.
  const wrappingClass = maxWidth
    ? 'tw-w-max tw-whitespace-normal tw-break-words'
    : 'tw-whitespace-nowrap'

  // Pusty tekst = brak dymka (razem z wyzwalaczem). `return` musi stać pod hookami:
  // gdy `text` przychodzi asynchronicznie, warunek się zmienia i React przy wcześniejszym
  // ustawieniu wywalał „Rendered more hooks than during the previous render".
  if (!label) return null

  return (
    <div
      className={`tw-relative tw-inline-flex tw-items-center tw-justify-center ${disabled || blocked ? 'tw-cursor-not-allowed' : ''}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <TriggerTag
        ref={triggerRef}
        disabled={disabled}
        onClick={blocked ? undefined : onClick && onClick}
        type="button"
        className={`tw-relative tw-cursor-pointer ${!checked ? 'tw-opacity-50' : ''} ${disabled || blocked ? 'tw-opacity-40' : ''} ${disabled ? 'tw-pointer-events-none' : ''} ${className}`}
      >
        {children}
      </TriggerTag>
      {checked && showDot && (
        <div className="tw-absolute -tw-bottom-1 tw-left-1/2 tw-h-1.5 tw-w-1.5 tw--translate-x-1/2 tw-translate-y-1/2 tw-rounded-full tw-bg-primary" />
      )}

      {portal ? (
        createPortal(
          <div
            ref={tooltipRef}
            style={{
              maxWidth,
              top: fixedPosition?.top ?? 0,
              left: fixedPosition?.left ?? 0,
              // Przed pierwszym pomiarem dymek jest niewidoczny, ale musi sie wyrenderowac,
              // zeby bylo co zmierzyc - inaczej mignalby w lewym gornym rogu.
              visibility: fixedPosition ? 'visible' : 'hidden',
              ...(zIndex != null ? { zIndex } : null),
            }}
            className={`${visible ? 'tw-opacity-90' : 'tw-opacity-0'} tw-pointer-events-none tw-fixed tw-select-none tw-rounded tw-bg-neutral-900 tw-p-1.5 tw-transition-opacity ${wrappingClass} ${zIndex == null ? 'tw-z-[100]' : ''}`}
          >
            <p className="tw-text-sm tw-text-secondary">{label}</p>
          </div>,
          document.body,
        )
      ) : (
        <div
          ref={tooltipRef}
          style={maxWidth ? { maxWidth } : undefined}
          className={`${visible ? 'tw-opacity-80' : 'tw-opacity-0'} tw-pointer-events-none tw-absolute tw-z-50 tw-select-none tw-rounded tw-bg-neutral-900 tw-p-1 tw-transition-all ${wrappingClass} ${
            vPos === 'bottom' ? 'tw-top-full tw-mt-4' : 'tw-bottom-full tw-mb-4'
          } ${
            align === 'start' || hPos === 'right'
              ? 'tw-left-0'
              : hPos === 'left'
                ? 'tw-right-0'
                : 'tw-left-1/2 tw--translate-x-1/2'
          }`}
        >
          <p className="tw-text-sm tw-text-secondary">{label}</p>

          <div
            className={`tw-absolute tw-h-2 tw-w-2 tw-rotate-45 tw-bg-neutral-900 ${
              vPos === 'bottom' ? 'tw-top-[-4px]' : 'tw-bottom-[-4px]'
            } ${
              align === 'start' || hPos === 'right'
                ? 'tw-left-3'
                : hPos === 'left'
                  ? 'tw-right-3'
                  : 'tw-left-1/2 tw--translate-x-1/2'
            }`}
          />
        </div>
      )}
    </div>
  )
}

export default Tooltip
